import React from 'react';
import { useHistory } from 'react-router';
import { useFormik } from 'formik';
import { Button, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';
import { CreateForm, EditForm } from './schemas/PermissionForm';
import Permission from 'types/models/Permission';
import UserRoleType from 'types/enums/UserRoleType';
import { mapToSelectOptions } from 'utils/select';
import { updatePermissions } from 'services/permissionServices';
import usePermissions from 'hooks/usePermissions';

export const rolesFromEnum = Object.entries(UserRoleType)
  .filter(([key]) => isNaN(Number(key)))
  .map(([key, value]) => ({
    id: value,
    description: key,
  }));

export default ({ permission }: { permission: Permission }): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = React.useState(false);
  const { setupPermissions } = usePermissions();

  const onCancel = (): void => {
    formik.setSubmitting(false);
  };

  const onConfirm = async (): Promise<void> => {
    const payload = {
      id: permission.id,
      ...formik.values,
    } as any;

    try {
      let result: Permission | null = null;
      if (permission) {
        result = await updatePermissions(permission.id, payload);
      }
      if (result) {
        await setupPermissions();
        history.go(-1);
      }
    } catch {
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: permission?.name ?? '',
      roles: permission?.roles?.map((role) => role.id) ?? [],
    },
    validationSchema: permission ? EditForm(t) : CreateForm(t),
    onSubmit: () => {
      setOpenDialog(true);
    },
  });

  return (
    <>
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <form onSubmit={formik.handleSubmit}>
        <TextField
          margin="dense"
          fullWidth
          id="name"
          name="name"
          label={t('name')}
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          disabled={formik.isSubmitting}
        />

        <TextField
          margin="dense"
          fullWidth
          id="name"
          name="name"
          label={t('code')}
          value={permission.code}
          disabled={true}
        />

        <SelectField
          name="roles"
          label={t('permissions.roles-label')}
          placeholder={t('permissions.roles-placeholder')}
          value={formik.values.roles}
          options={mapToSelectOptions(
            rolesFromEnum.filter(
              (role) =>
                ![UserRoleType.Buyer, UserRoleType.FranchiseEditor, UserRoleType.CommerceEditor].includes(
                  role.id as any,
                ),
            ),
            {
              label: 'description',
            },
          )}
          isMulti={true}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.roles}
          error={formik.errors.roles}
          isClearable={true}
          backspaceRemovesValue={true}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />

        <div className="footer">
          <Button className="btn-50" variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
            {t('save-changes')}
          </Button>
        </div>
      </form>
    </>
  );
};
